
import React, { useEffect, useState, useRef } from 'react';
import api from '../../utils/api';

let cachedCats = null;

const G = '#1A6B3C';

const COLOR_MAP = {
  vegetables:'#16A34A', fruits:'#DC2626', grains:'#D97706',
  spices:'#B45309', flowers:'#DB2777', 'leafy greens':'#059669',
  pulses:'#7C3AED', herbs:'#0891B2', mushrooms:'#92400E', other:'#6B7280',
};

export default function CategoryStrip({ onCategorySelect, onSubCategorySelect, activeCategory }) {
  const [cats, setCats]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeId, setActiveId] = useState(null);
  const stripRef = useRef();
  const dropRef  = useRef();

  useEffect(() => {
    loadCategories();
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          stripRef.current && !stripRef.current.contains(e.target)) {
        setActiveId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keep local highlight in sync if the parent clears/changes the active category externally
  useEffect(() => {
    if (!activeCategory) { setActiveId(null); return; }
    const match = cats.find(c => c.name?.toLowerCase() === activeCategory.toLowerCase());
    setActiveId(match ? match._id : null);
  }, [activeCategory, cats]);

  const loadCategories = async () => {
    if (cachedCats) { setCats(cachedCats); setLoading(false); return; } // ← use cache
    try {
      const { data } = await api.get('/categories');
      cachedCats = (data.categories || []).filter(c => c.isActive); // ← save to cache
      setCats(cachedCats);
    } catch {
      setCats([]);
    } finally { setLoading(false); }
  };

  const handleCatClick = (cat) => {
    if (activeId === cat._id) {
      setActiveId(null);
    } else {
      setActiveId(cat._id);
      onCategorySelect && onCategorySelect(cat.name);
    }
  };

  const handleSubClick = (cat, subName) => {
    setActiveId(null);
    onSubCategorySelect && onSubCategorySelect(cat.name, subName);
  };

  const activeCat = cats.find(c => c._id === activeId);

  return (
    <div style={{ position:'relative', zIndex:50, fontFamily:'Inter,system-ui,sans-serif' }}>

      {/* ── Category horizontal strip ── */}
      <div
        ref={stripRef}
        style={{
          display:'flex', overflowX:'auto', gap:8,
          padding:'8px 14px 8px', background:'white',
          borderBottom:'0.5px solid #E5E7EB', scrollbarWidth:'none',
        }}
      >
        <style>{`div::-webkit-scrollbar{display:none}`}</style>

        {loading ? (
          Array(5).fill(0).map((_, i) => (
            <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flexShrink:0 }}>
              <div style={{ width:60, height:60, borderRadius:'50%', background:'#F1F3EE' }} />
              <div style={{ width:44, height:8, borderRadius:4, background:'#F1F3EE' }} />
            </div>
          ))
        ) : cats.map(cat => {
          const isActive = activeId === cat._id;
          const color    = cat.color || COLOR_MAP[cat.name?.toLowerCase()] || G;
          return (
            <button
              key={cat._id}
              onClick={() => handleCatClick(cat)}
              style={{
                display:'flex', flexDirection:'column', alignItems:'center', gap:6,
                border:'none', background:'none', padding:0,
                cursor:'pointer', flexShrink:0, transition:'transform .15s',
                transform: isActive ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              {/* Circle — image if available, else icon emoji */}
              <div style={{
                width:60, height:60, borderRadius:'50%',
                background: isActive ? color+'1a' : '#F5F7FA',
                border:`2px solid ${isActive ? color : '#E5E7EB'}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:26, transition:'all .2s', overflow:'hidden',
                boxShadow: isActive ? `0 0 0 3px ${color}1a` : 'none',
              }}>
                {cat.image ? (
                  <img
                    src={cat.image}
                    alt={cat.name}
                    style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }}
                    onError={e => { e.target.style.display='none'; }}
                  />
                ) : cat.icon ? (
                  <span>{cat.icon}</span>
                ) : null}
              </div>

              {/* Label */}
              <span style={{
                fontSize:11.5, fontWeight: isActive ? 700 : 600,
                color: isActive ? color : '#374151',
                maxWidth:66, textAlign:'center', lineHeight:1.25,
              }}>
                {cat.name}
              </span>

              {/* Active dot */}
              {isActive && <div style={{ width:5, height:5, borderRadius:'50%', background:color, marginTop:-3 }} />}
            </button>
          );
        })}
      </div>

      {/* ── Sub-category dropdown ── */}
      {activeCat && (
        <div
          ref={dropRef}
          style={{
            position:'absolute', top:'100%', left:0, right:0,
            background:'white', borderBottom:`3px solid ${activeCat.color||G}`,
            boxShadow:'0 8px 24px rgba(0,0,0,.12)',
            padding:'12px 14px 14px', zIndex:100,
            animation:'slideDown .18s ease-out',
          }}
        >
          <style>{`
            @keyframes slideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
          `}</style>

          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {/* Category image or icon in header */}
              <div style={{ width:30, height:30, borderRadius:'50%', overflow:'hidden', background:(activeCat.color||G)+'20', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                {activeCat.image
                  ? <img src={activeCat.image} alt={activeCat.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none'; }} />
                  : activeCat.icon ? <span>{activeCat.icon}</span> : null}
              </div>
              <span style={{ fontSize:14.5, fontWeight:800, color:'#1a1a1a' }}>{activeCat.name}</span>
              <span style={{ fontSize:11.5, color:'#9CA3AF' }}>({activeCat.subCategories?.length || 0} types)</span>
            </div>
            <button
              onClick={() => { setActiveId(null); onCategorySelect && onCategorySelect(activeCat.name); }}
              style={{ fontSize:11.5, color:activeCat.color||G, fontWeight:700, background:'none', border:'none', cursor:'pointer' }}>
              See all →
            </button>
          </div>

          {/* Sub-category grid */}
          <div style={{
            display:'grid', gridTemplateColumns:'repeat(4, minmax(0, 1fr))', gap:10,
            maxHeight:210, overflowY:'auto', scrollbarWidth:'thin',
          }}>
            {(activeCat.subCategories || []).map((sub, idx) => {
              const subName  = typeof sub === 'string' ? sub : (sub?.name  || '');
              const subIcon  = typeof sub === 'object'  ? (sub?.icon  || '') : '';
              const subImage = typeof sub === 'object'  ? (sub?.image || '')   : '';
              const accent   = activeCat.color || G;
              return (
                <button
                  key={subName + idx}
                  onClick={() => handleSubClick(activeCat, subName)}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center',
                    gap:5, padding:'8px 4px',
                    border:`1px solid ${accent}25`,
                    borderRadius:12, background:accent+'0a',
                    cursor:'pointer', transition:'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = accent+'18'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = accent+'0a'; }}
                >
                  {/* Sub image or emoji */}
                  <div style={{
                    width:44, height:44, borderRadius:'50%',
                    background:'white', border:`1.5px solid ${accent}30`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:20, overflow:'hidden',
                  }}>
                    {subImage ? (
                      <img
                        src={subImage}
                        alt={subName}
                        style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }}
                        onError={e => { e.target.style.display='none'; }}
                      />
                    ) : subIcon ? (
                      <span>{subIcon}</span>
                    ) : null}
                  </div>
                  <span style={{
                    fontSize:10, fontWeight:700,
                    color:'#374151',
                    textAlign:'center', lineHeight:1.2, wordBreak:'break-word',
                  }}>
                    {subName}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function getSubEmoji(name) {
  const n = (typeof name === 'string' ? name : String(name||'')).toLowerCase();
  const map = {
    tomato:'🍅', carrot:'🥕', potato:'🥔', onion:'🧅', cabbage:'🥬',
    spinach:'🥬', chilli:'🌶️', brinjal:'🍆', beetroot:'🟣', peas:'🫛',
    beans:'🫘', cucumber:'🥒', pumpkin:'🎃', capsicum:'🫑', cauliflower:'🥦',
    garlic:'🧄', corn:'🌽', 'bitter gourd':'🥒', 'green peas':'🫛',
    mango:'🥭', banana:'🍌', guava:'🍈', papaya:'🍈', watermelon:'🍉',
    grapes:'🍇', orange:'🍊', apple:'🍎', lemon:'🍋', pineapple:'🍍',
    pomegranate:'🔴', jackfruit:'🟡', coconut:'🥥',
    rice:'🍚', wheat:'', maize:'🌽', jowar:'', bajra:'', ragi:'',
    barley:'', sorghum:'', turmeric:'🟡', pepper:'⚫', cardamom:'🟢',
    coriander:'', cumin:'🟤', fenugreek:'🟤', mustard:'🟡', cloves:'🟤',
    marigold:'🌼', rose:'🌹', jasmine:'🌸', sunflower:'🌻', lotus:'🪷',
    hibiscus:'🌺', chrysanthemum:'🌸', tulsi:'', 'aloe vera':'🌵',
    neem:'', ashwagandha:'', brahmi:'', amla:'🟢', lemongrass:'🌾',
    'toor dal':'🟡', 'moong dal':'🟡', 'chana dal':'🟡', 'urad dal':'⚪',
    'masoor dal':'🔴', chickpeas:'🟡', 'kidney beans':'🔴',
    mint:'', 'curry leaves':'🍃', moringa:'', amaranth:'',
    'fenugreek leaves':'', pluckk:'', ginger:'🫚', other:'',
  };
  return map[n] || '';
}